import { CredentialsKeyEnum, type ICredentials } from '@novu/shared';
import { getAgentApiBaseUrl } from '@/config';

export type StepStatus = 'completed' | 'current' | 'upcoming';

export function buildAgentWebhookUrl(agentId: string, integrationIdentifier: string): string {
  return `${getAgentApiBaseUrl()}/v1/agents/${agentId}/webhook/${integrationIdentifier}`;
}

/**
 * Builds an `sms:` deep link that opens the device's Messages app (iMessage on
 * iOS/macOS, SMS elsewhere) pre-addressed to the agent's Sendblue number with a
 * greeting pre-filled in the body. Lets a user start the conversation manually
 * as an alternative to the automatic test-message send.
 */
export function buildImessageFallbackHref(fromNumber: string, agentName: string): string {
  const body = `Hi ${agentName}, how can you help?`;

  return `sms:${encodeURIComponent(fromNumber)}?body=${encodeURIComponent(body)}`;
}

export function deriveStepStatus(stepIndex: number, firstIncompleteStep: number): StepStatus {
  if (stepIndex < firstIncompleteStep) return 'completed';
  if (stepIndex === firstIncompleteStep) return 'current';

  return 'upcoming';
}

/**
 * Returns true when at least one string credential field has been filled in.
 * Boolean fields (secure, requireTls, etc.) are excluded — their default false
 * value should not count as "credentials saved".
 */
export function hasIntegrationCredentials(credentials: ICredentials | undefined): boolean {
  if (!credentials) return false;

  return Object.values(credentials).some((v) => typeof v === 'string' && v.length > 0);
}

/**
 * Returns true only when the user-input Sendblue credentials have been saved.
 * The webhook secret (`token`) is auto-generated by Novu during the Configure
 * webhook step, so generic credential presence would complete step 1 early.
 */
export function hasSendblueUserCredentials(credentials: ICredentials | undefined): boolean {
  if (!credentials) return false;

  const requiredKeys = [CredentialsKeyEnum.ApiKey, CredentialsKeyEnum.SecretKey, CredentialsKeyEnum.From] as const;

  return requiredKeys.every((key) => {
    const value = credentials[key];

    return typeof value === 'string' && value.trim().length > 0;
  });
}

/**
 * Returns true only when the user-input WhatsApp credentials have been saved.
 * The verify `token` is auto-generated by Novu at integration creation time, so
 * checking generic credential presence would mark this step complete prematurely.
 *
 * `secretKey` (App Secret) is required because the backend rejects WhatsApp
 * adapter creation without it — marking the step complete without it would
 * leave the user stranded at "Connect & test".
 */
export function hasWhatsAppUserCredentials(credentials: ICredentials | undefined): boolean {
  if (!credentials) return false;

  if (credentials.isNovuManaged === true) {
    const requiredKeys = [
      CredentialsKeyEnum.ApiToken,
      CredentialsKeyEnum.phoneNumberIdentification,
      CredentialsKeyEnum.businessAccountId,
    ] as const;

    return requiredKeys.every((key) => {
      const value = credentials[key];

      return typeof value === 'string' && value.trim().length > 0;
    });
  }

  const requiredKeys = [
    CredentialsKeyEnum.ApiToken,
    CredentialsKeyEnum.phoneNumberIdentification,
    CredentialsKeyEnum.businessAccountId,
    CredentialsKeyEnum.SecretKey,
  ] as const;

  return requiredKeys.every((key) => {
    const value = credentials[key];

    return typeof value === 'string' && value.trim().length > 0;
  });
}
